import type { BlockKind, ICalFeed, ResourceBlock } from '@stayafrica/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  Dialog,
  DialogClose,
  DialogContent,
  EmptyState,
  ErrorState,
  FormField,
  IconButton,
  Input,
  NoPermissionState,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  toast,
} from '@stayafrica/ui';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Ban, Pencil, Plus, RefreshCw, Rss, Trash2, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan } from '@/features/auth/session';
import { BlockFormDrawer } from '@/features/resources/BlockFormDrawer';
import {
  createICalFeed,
  deleteICalFeed,
  deleteResourceBlock,
  icalFeedsQueryOptions,
  resourceBlocksQueryOptions,
  resourceKeys,
  resourcesQueryOptions,
  syncICalFeed,
  type BlocksFilters,
} from '@/features/resources/queries';
import { BLOCK_KINDS, blockBadgeTone } from '@/features/resources/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime, formatRelative } from '@/lib/format';

/** `?new=block` ouvre directement le tiroir de blocage (palette ⌘K). */
export const Route = createFileRoute('/_app/resources/maintenance')({
  validateSearch: (search: Record<string, unknown>): { new?: 'block' } =>
    search['new'] === 'block' ? { new: 'block' } : {},
  component: MaintenancePage,
});

type PeriodFilter = 'all' | 'upcoming' | 'current' | 'past';

function MaintenancePage() {
  const { t, i18n } = useTranslation(['maintenance', 'resources', 'common', 'errors']);
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const canRead = useCan('availability.read');
  const canBlock = useCan('availability.block');

  const [kind, setKind] = useState<BlockKind | undefined>(undefined);
  const [resourceId, setResourceId] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<PeriodFilter>('all');
  const [drawer, setDrawer] = useState<{
    open: boolean;
    block?: ResourceBlock;
    preset?: { resourceId?: string };
  }>({ open: false });
  const [toDelete, setToDelete] = useState<ResourceBlock | null>(null);
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedToDelete, setFeedToDelete] = useState<ICalFeed | null>(null);

  useEffect(() => {
    if (search.new !== 'block') return;
    setDrawer({ open: true });
    void navigate({ to: '/resources/maintenance', search: {}, replace: true });
  }, [search.new, navigate]);

  const filters: BlocksFilters = {
    ...(kind ? { kind } : {}),
    ...(resourceId ? { resourceId } : {}),
  };

  const blocksQuery = useInfiniteQuery({
    ...resourceBlocksQueryOptions(filters),
    enabled: canRead,
  });
  const resourcesQuery = useInfiniteQuery({ ...resourcesQueryOptions({}), enabled: canRead });
  const feedsQuery = useQuery({ ...icalFeedsQueryOptions(), enabled: canRead });

  const deleteMutation = useMutation({
    mutationFn: (block: ResourceBlock) => deleteResourceBlock(block.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('maintenance:toasts.blockDeleted'));
      setToDelete(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const syncMutation = useMutation({
    mutationFn: (feed: ICalFeed) => syncICalFeed(feed.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all });
      toast.success(t('maintenance:toasts.feedSynced'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const deleteFeedMutation = useMutation({
    mutationFn: (feed: ICalFeed) => deleteICalFeed(feed.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.icalFeeds });
      toast.success(t('maintenance:toasts.feedDeleted'));
      setFeedToDelete(null);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const resources = resourcesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const feeds = feedsQuery.data ?? [];
  const now = Date.now();
  const allBlocks = blocksQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const blocks = allBlocks.filter((block) => {
    const start = new Date(block.startsAt).getTime();
    const end = new Date(block.endsAt).getTime();
    if (period === 'upcoming') return start > now;
    if (period === 'current') return start <= now && end > now;
    if (period === 'past') return end <= now;
    return true;
  });
  const filtered = Boolean(kind || resourceId || period !== 'all');

  return (
    <Container>
      <PageHeader
        title={t('maintenance:title')}
        subtitle={t('maintenance:subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('resources:title'),
            link: <Link to="/resources">{t('resources:title')}</Link>,
          },
          { label: t('maintenance:title') },
        ]}
        actions={
          <Can permission="availability.block">
            <Button variant="primary" onClick={() => setDrawer({ open: true })}>
              <Plus className="ico" aria-hidden="true" />
              {t('maintenance:create')}
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <Card>
          <CardHeader
            title={t('maintenance:title')}
            icon={<Wrench className="ico ico-16" aria-hidden="true" />}
            iconTone="red"
          />

          <Toolbar>
            <ChipFilter active={kind === undefined} onClick={() => setKind(undefined)}>
              {t('maintenance:filters.all')}
            </ChipFilter>
            {([...BLOCK_KINDS, 'EXTERNAL'] as BlockKind[]).map((value) => (
              <ChipFilter
                key={value}
                active={kind === value}
                onClick={() => setKind(kind === value ? undefined : value)}
              >
                {t(`maintenance:kinds.${value}`)}
              </ChipFilter>
            ))}

            <span className="spacer" />

            <label className="sr-only" htmlFor="block-resource">
              {t('maintenance:filters.resource')}
            </label>
            <select
              id="block-resource"
              className="select"
              style={{ width: 'auto', height: 30, fontSize: 'var(--fs-13)' }}
              value={resourceId ?? ''}
              onChange={(event) => setResourceId(event.target.value || undefined)}
            >
              <option value="">{t('maintenance:filters.allResources')}</option>
              {resources.map((resource) => (
                <option key={resource.id} value={resource.id}>
                  {resource.name}
                </option>
              ))}
            </select>

            <label className="sr-only" htmlFor="block-period">
              {t('maintenance:filters.period')}
            </label>
            <select
              id="block-period"
              className="select"
              style={{ width: 'auto', height: 30, fontSize: 'var(--fs-13)' }}
              value={period}
              onChange={(event) => setPeriod(event.target.value as PeriodFilter)}
            >
              <option value="all">{t('maintenance:filters.all')}</option>
              <option value="current">{t('maintenance:filters.current')}</option>
              <option value="upcoming">{t('maintenance:filters.upcoming')}</option>
              <option value="past">{t('maintenance:filters.past')}</option>
            </select>
          </Toolbar>

          {blocksQuery.isPending ? (
            <TableSkeleton rows={4} columns={5} label={t('common:states.loading')} />
          ) : blocksQuery.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(blocksQuery.error, t)}
              traceId={errorTraceId(blocksQuery.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void blocksQuery.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : blocks.length === 0 ? (
            <EmptyState
              icon={<Ban className="ico ico-24" aria-hidden="true" />}
              title={filtered ? t('maintenance:emptyFilteredTitle') : t('maintenance:emptyTitle')}
              description={
                filtered ? t('maintenance:emptyFilteredBody') : t('maintenance:emptyBody')
              }
              action={
                filtered ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setKind(undefined);
                      setResourceId(undefined);
                      setPeriod('all');
                    }}
                  >
                    {t('common:actions.clearFilters')}
                  </Button>
                ) : (
                  <Can permission="availability.block">
                    <Button variant="primary" onClick={() => setDrawer({ open: true })}>
                      <Plus className="ico" aria-hidden="true" />
                      {t('maintenance:create')}
                    </Button>
                  </Can>
                )
              }
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('maintenance:columns.resource')}</Th>
                    <Th>{t('maintenance:columns.kind')}</Th>
                    <Th>{t('maintenance:columns.period')}</Th>
                    <Th>{t('maintenance:columns.reason')}</Th>
                    <Th>{t('maintenance:columns.createdBy')}</Th>
                    <Th align="right">
                      <span className="sr-only">{t('maintenance:columns.actions')}</span>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((block) => (
                    <Tr key={block.id}>
                      <Td strong>
                        <Link to="/resources/$resourceId" params={{ resourceId: block.resourceId }}>
                          {block.resourceName}
                        </Link>
                      </Td>
                      <Td>
                        <Badge tone={blockBadgeTone(block.kind)}>
                          {t(`maintenance:kinds.${block.kind}`)}
                        </Badge>
                      </Td>
                      <Td className="t-13">
                        {formatDateTime(block.startsAt, i18n.language)} →{' '}
                        {formatDateTime(block.endsAt, i18n.language)}
                      </Td>
                      <Td className="t-13">{block.reason ?? '—'}</Td>
                      <Td className="t-13">{block.createdByName ?? block.source ?? '—'}</Td>
                      <Td align="right">
                        {canBlock && block.kind !== 'EXTERNAL' ? (
                          <span className="row-actions">
                            <IconButton
                              label={`${t('common:actions.edit')} — ${block.resourceName}`}
                              onClick={() => setDrawer({ open: true, block })}
                            >
                              <Pencil className="ico ico-16" aria-hidden="true" />
                            </IconButton>
                            <IconButton
                              label={`${t('common:actions.delete')} — ${block.resourceName}`}
                              onClick={() => setToDelete(block)}
                            >
                              <Trash2 className="ico ico-16" aria-hidden="true" />
                            </IconButton>
                          </span>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>

        {/* ---------------- Flux iCal ---------------- */}
        <Card>
          <CardHeader
            title={t('maintenance:ical.title')}
            icon={<Rss className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              canBlock ? (
                <Button variant="secondary" size="sm" onClick={() => setFeedOpen(true)}>
                  <Plus className="ico ico-14" aria-hidden="true" />
                  {t('maintenance:ical.add')}
                </Button>
              ) : null
            }
          />
          <CardBody density="flush">
            <p className="hint" style={{ padding: '0 var(--s-4)' }}>
              {t('maintenance:ical.subtitle')}
            </p>
          </CardBody>
          {feeds.length === 0 ? (
            <EmptyState
              icon={<Rss className="ico ico-24" aria-hidden="true" />}
              title={t('maintenance:ical.empty')}
              description={t('maintenance:ical.emptyBody')}
            />
          ) : (
            <TableWrap>
              <Table compact>
                <thead>
                  <tr>
                    <Th>{t('maintenance:ical.columns.resource')}</Th>
                    <Th>{t('maintenance:ical.columns.source')}</Th>
                    <Th>{t('maintenance:ical.columns.lastSync')}</Th>
                    <Th>{t('maintenance:ical.columns.status')}</Th>
                    <Th align="right">
                      <span className="sr-only">{t('maintenance:columns.actions')}</span>
                    </Th>
                  </tr>
                </thead>
                <tbody>
                  {feeds.map((feed) => (
                    <Tr key={feed.id}>
                      <Td strong>{feed.resourceName}</Td>
                      <Td className="t-13">
                        {t(`maintenance:ical.sources.${feed.source}`)}
                        <div className="t-12 muted truncate" style={{ maxWidth: 320 }}>
                          {feed.url}
                        </div>
                      </Td>
                      <Td className="t-13">
                        {feed.lastSyncAt
                          ? formatRelative(feed.lastSyncAt, i18n.language)
                          : t('maintenance:ical.never')}
                      </Td>
                      <Td>
                        {feed.lastSyncStatus === 'OK' ? (
                          <Badge tone="green" dot>
                            {t('maintenance:ical.ok')}
                          </Badge>
                        ) : feed.lastSyncStatus === 'ERROR' ? (
                          <Badge tone="red" dot>
                            {t('maintenance:ical.error')}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">{t('maintenance:ical.never')}</Badge>
                        )}
                        {feed.lastSyncMessage ? (
                          <div className="t-12 muted">{feed.lastSyncMessage}</div>
                        ) : null}
                      </Td>
                      <Td align="right">
                        {canBlock ? (
                          <span className="row-actions">
                            <IconButton
                              label={`${t('maintenance:ical.sync')} — ${feed.resourceName}`}
                              onClick={() => syncMutation.mutate(feed)}
                            >
                              <RefreshCw className="ico ico-16" aria-hidden="true" />
                            </IconButton>
                            <IconButton
                              label={`${t('common:actions.delete')} — ${feed.resourceName}`}
                              onClick={() => setFeedToDelete(feed)}
                            >
                              <Trash2 className="ico ico-16" aria-hidden="true" />
                            </IconButton>
                          </span>
                        ) : null}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </div>

      <BlockFormDrawer
        open={drawer.open}
        onOpenChange={(open) => setDrawer((current) => ({ ...current, open }))}
        resources={resources}
        block={drawer.block}
        preset={drawer.preset}
      />

      <ICalFeedDialog open={feedOpen} onOpenChange={setFeedOpen} resources={resources} />

      <Dialog open={toDelete !== null} onOpenChange={(open) => !open && setToDelete(null)}>
        {toDelete ? (
          <DialogContent
            title={t('maintenance:delete.title')}
            description={t('maintenance:delete.body')}
            icon={<Trash2 className="ico ico-16" aria-hidden="true" />}
            iconTone="red"
            closeLabel={t('common:actions.close')}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  loading={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(toDelete)}
                >
                  {t('maintenance:delete.confirm')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>

      <Dialog open={feedToDelete !== null} onOpenChange={(open) => !open && setFeedToDelete(null)}>
        {feedToDelete ? (
          <DialogContent
            title={t('maintenance:ical.deleteTitle')}
            description={t('maintenance:ical.deleteBody')}
            icon={<Trash2 className="ico ico-16" aria-hidden="true" />}
            iconTone="red"
            closeLabel={t('common:actions.close')}
            footer={
              <>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:actions.cancel')}</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  loading={deleteFeedMutation.isPending}
                  onClick={() => deleteFeedMutation.mutate(feedToDelete)}
                >
                  {t('common:actions.delete')}
                </Button>
              </>
            }
          />
        ) : null}
      </Dialog>
    </Container>
  );
}

/** Modale d'ajout d'un flux iCal (ressource, plateforme, URL d'export). */
function ICalFeedDialog({
  open,
  onOpenChange,
  resources,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resources: { id: string; name: string }[];
}) {
  const { t } = useTranslation(['maintenance', 'common', 'errors']);
  const queryClient = useQueryClient();
  const [resourceId, setResourceId] = useState('');
  const [source, setSource] = useState<ICalFeed['source']>('BOOKING_COM');
  const [url, setUrl] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      createICalFeed({ resourceId: resourceId || resources[0]?.id || '', source, url }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: resourceKeys.icalFeeds });
      toast.success(t('maintenance:toasts.feedCreated'));
      setUrl('');
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={t('maintenance:ical.addTitle')}
        description={t('maintenance:ical.subtitle')}
        icon={<Rss className="ico ico-16" aria-hidden="true" />}
        iconTone="blue"
        closeLabel={t('common:actions.close')}
        footer={
          <>
            <DialogClose asChild>
              <Button variant="ghost">{t('common:actions.cancel')}</Button>
            </DialogClose>
            <Button
              variant="primary"
              loading={mutation.isPending}
              disabled={!url}
              onClick={() => mutation.mutate()}
            >
              {t('common:actions.create')}
            </Button>
          </>
        }
      >
        <div className="stack">
          <FormField label={t('maintenance:ical.columns.resource')}>
            {(field) => (
              <Select
                id={field.id}
                value={resourceId || resources[0]?.id || ''}
                onValueChange={setResourceId}
                options={resources.map((resource) => ({
                  value: resource.id,
                  label: resource.name,
                }))}
              />
            )}
          </FormField>
          <FormField label={t('maintenance:ical.columns.source')}>
            {(field) => (
              <Select
                id={field.id}
                value={source}
                onValueChange={(value) => setSource(value as ICalFeed['source'])}
                options={[
                  { value: 'BOOKING_COM', label: t('maintenance:ical.sources.BOOKING_COM') },
                  { value: 'AIRBNB', label: t('maintenance:ical.sources.AIRBNB') },
                  { value: 'OTHER', label: t('maintenance:ical.sources.OTHER') },
                ]}
              />
            )}
          </FormField>
          <FormField label={t('maintenance:ical.urlLabel')} hint={t('maintenance:ical.urlHint')}>
            {(field) => (
              <Input
                id={field.id}
                type="url"
                value={url}
                aria-describedby={field.describedBy}
                placeholder="https://ical.booking.com/v1/export?t=…"
                onChange={(event) => setUrl(event.target.value)}
              />
            )}
          </FormField>
        </div>
      </DialogContent>
    </Dialog>
  );
}
